// Founder P0 2026-09 — Mi Portal training area: employee sees assigned
// training actions and declares completion (not a competency proof).
import React, { useCallback, useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Card } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { toast } from 'sonner';
import { api, formatApiError } from '@/lib/api';
import { GraduationCap, ExternalLink, CheckCircle2, Loader2 } from 'lucide-react';

const STATUS_TONE = {
  planned: 'bg-sky-100 text-sky-800',
  in_progress: 'bg-amber-100 text-amber-800',
  completed: 'bg-emerald-100 text-emerald-800',
};

export const MyTraining = () => {
  const { t, i18n } = useTranslation();
  const isES = (i18n.language || '').startsWith('es');
  const L = (es, en) => (isES ? es : en);
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [busy, setBusy] = useState(null);

  const load = useCallback(async () => {
    try {
      const { data } = await api.get('/me/training');
      setItems(data || []);
    } catch { /* section stays empty */ } finally { setLoading(false); }
  }, []);
  useEffect(() => { load(); }, [load]);

  const complete = async (a) => {
    setBusy(a.id);
    try {
      await api.post(`/me/training/${a.id}/complete`);
      toast.success(L('Capacitación marcada como completada.', 'Training marked as completed.'));
      load();
    } catch (e) { toast.error(formatApiError(e)); } finally { setBusy(null); }
  };

  if (loading || items.length === 0) return null;

  return (
    <Card className="p-6 shadow-card" data-testid="me-training-card">
      <div className="flex items-center gap-2 mb-4">
        <GraduationCap className="h-5 w-5 text-primary" />
        <h2 className="text-lg font-semibold">
          {t('mePortal.training.title', { defaultValue: L('Mis capacitaciones', 'My training') })}
        </h2>
      </div>
      <div className="space-y-3">
        {items.map((a) => (
          <div key={a.id} className="rounded-lg border border-border p-4 flex flex-wrap items-start gap-3"
               data-testid={`me-training-${a.id}`}>
            <div className="flex-1 min-w-[220px] space-y-1">
              <p className="font-semibold">{a.name}</p>
              <p className="text-sm text-muted-foreground">
                {[a.type, a.provider].filter(Boolean).join(' · ')}
                {(a.start_date || a.end_date) && (
                  <span> · {a.start_date || '…'} → {a.end_date || '…'}</span>
                )}
              </p>
              {a.evidence_location && /^https?:\/\//.test(a.evidence_location) && (
                <a href={a.evidence_location} target="_blank" rel="noopener noreferrer"
                   className="inline-flex items-center gap-1 text-sm text-primary underline underline-offset-2"
                   data-testid={`me-training-link-${a.id}`}>
                  <ExternalLink className="h-3.5 w-3.5" />
                  {L('Abrir recurso de la capacitación', 'Open training resource')}
                </a>
              )}
            </div>
            <div className="flex items-center gap-2">
              <Badge className={`${STATUS_TONE[a.status] || 'bg-muted'} uppercase text-xs`}>
                {a.status === 'completed'
                  ? L('Completada', 'Completed')
                  : a.status === 'in_progress' ? L('En curso', 'In progress') : L('Asignada', 'Assigned')}
              </Badge>
              {a.status !== 'completed' && (
                <Button size="sm" onClick={() => complete(a)} disabled={busy === a.id}
                        className="gap-1.5" data-testid={`me-training-complete-${a.id}`}>
                  {busy === a.id ? <Loader2 className="h-4 w-4 animate-spin" /> : <CheckCircle2 className="h-4 w-4" />}
                  {L('He completado esta capacitación', 'I have completed this training')}
                </Button>
              )}
              {a.status === 'completed' && a.employee_completed_at && (
                <span className="text-xs text-muted-foreground">
                  {new Date(a.employee_completed_at).toLocaleDateString(isES ? 'es' : 'en')}
                </span>
              )}
            </div>
          </div>
        ))}
      </div>
      <p className="text-xs text-muted-foreground mt-3">
        {L('Confirmar la capacitación registra que la completaste; la evaluación de conocimientos, si aplica, se gestiona por separado.',
           'Confirming records that you completed the training; knowledge assessment, if applicable, is managed separately.')}
      </p>
    </Card>
  );
};

export default MyTraining;
